import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { AppProvider } from './context';
import { ToastProvider, useToast } from './components/ToastContext';
import { CallProvider } from './context/CallContext';
import { SessionProvider } from './context/SessionContext';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import AuthRedirect from './components/AuthRedirect';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CustomToaster } from './components/ui/Toast';
import { QueryProvider } from './providers/QueryProvider';
import ConnectionStatus from './components/ConnectionStatus';
import { connectionService } from './services/ConnectionService';
import OfflineNotification from './components/OfflineNotification';
import { Loader } from 'lucide-react';
import { PWAInstallPrompt, PWAUpdatePrompt } from './components/PWAInstallPrompt';
import useSocketAuth from './hooks/useSocketAuth';
import monitoring from './utils/monitoring';
import { usePerformanceDetection } from './hooks/usePerformanceDetection';
import { usePerformanceMode } from './store/uiStore';
import { MotionConfig } from 'framer-motion';
import i18n from './i18n';

// --- Lazy Loaded Pages ---
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const MFASetup = lazy(() => import('./pages/auth/MFASetup'));
const PasswordReset = lazy(() => import('./pages/auth/PasswordReset'));
const SSOCallback = lazy(() => import('./pages/auth/SSOCallback'));
const PatientDashboard = lazy(() => import('./pages/patient/Dashboard'));
const PatientAppointments = lazy(() => import('./pages/patient/Appointments'));
const PatientConsultation = lazy(() => import('./pages/patient/Consultation'));
const PatientWaitingRoom = lazy(() => import('./pages/patient/WaitingRoom'));
const PatientMedicalRecords = lazy(() => import('./pages/patient/MedicalRecords'));
const PatientMessages = lazy(() => import('./pages/patient/Messages'));
const PatientPrescriptions = lazy(() => import('./pages/patient/Prescriptions'));
const PatientCheckout = lazy(() => import('./pages/patient/Checkout'));
const DoctorsList = lazy(() => import('./pages/patient/DoctorsList'));
const DoctorDashboard = lazy(() => import('./pages/doctor/Dashboard'));
const DoctorAppointments = lazy(() => import('./pages/doctor/Appointments'));
const DoctorNewConsultation = lazy(() => import('./pages/doctor/NewConsultation'));
const DoctorTeleconsultation = lazy(() => import('./pages/doctor/Consultation'));
const DoctorPatientRecords = lazy(() => import('./pages/doctor/PatientRecords'));
const DoctorMessages = lazy(() => import('./pages/doctor/Messages'));
const DoctorPatientList = lazy(() => import('./pages/doctor/PatientList'));
const DoctorDirectory = lazy(() => import('./pages/doctor/DoctorDirectory'));
const DoctorPrescriptions = lazy(() => import('./pages/doctor/Prescriptions'));
const PeerConsultationLobby = lazy(() => import('./pages/doctor/PeerConsultationLobby'));
const DoctorWaitingRoom = lazy(() => import('./pages/doctor/WaitingRoom'));
const DoctorOnboarding = lazy(() => import('./pages/doctor/Onboarding'));
const NurseDashboard = lazy(() => import('./pages/nurse/Dashboard'));
const TriageQueue = lazy(() => import('./pages/nurse/TriageQueue'));
const ClinicDashboard = lazy(() => import('./pages/clinic/Dashboard'));
const ClinicQueuePage = lazy(() => import('./pages/clinic/QueuePage'));
const ClinicBillingPage = lazy(() => import('./pages/clinic/BillingPage'));
const ReferralsManager = lazy(() => import('./pages/clinic/Referrals'));
const ClinicPractitioners = lazy(() => import('./pages/clinic/Practitioners'));
const ClinicWorkspaces = lazy(() => import('./pages/clinic/Workspaces'));
const ClinicSettings = lazy(() => import('./pages/clinic/Settings'));
const PatientDMEPage = lazy(() => import('./pages/clinic/PatientDME'));
const QueueDisplayTV = lazy(() => import('./pages/clinic/QueueDisplayTV'));
const ClinicLogin = lazy(() => import('./pages/auth/ClinicLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminDoctors = lazy(() => import('./pages/admin/Doctors'));
const MonitoringDashboard = lazy(() => import('./pages/admin/MonitoringDashboard'));
const ConsultationModeration = lazy(() => import('./pages/admin/ConsultationModeration'));
const AdminServices = lazy(() => import('./pages/admin/Services'));
const AdminFinancialReports = lazy(() => import('./pages/admin/FinancialReports'));
const AdminConsultations = lazy(() => import('./pages/admin/Consultations'));
const AdminPrescriptions = lazy(() => import('./pages/admin/Prescriptions'));
const AdminSecurity = lazy(() => import('./pages/admin/SecurityManagement'));
const AdminCommunications = lazy(() => import('./pages/admin/Communications'));
const AdminPermissions = lazy(() => import('./pages/admin/Permissions'));
const AdminDataExport = lazy(() => import('./pages/admin/DataExportBackup'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const ProfilePage = lazy(() => import('./pages/Profile'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));

// Test components supprimés - utiliser les composants de production

// A simple loading component to show during lazy loading
const LoadingFallback = () => (
  <div className="flex h-screen w-full items-center justify-center bg-med-primary-deep relative overflow-hidden">
    {/* AURORA DECOR */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-br from-med-primary/20 via-transparent to-med-success/10 blur-[120px] pointer-events-none"></div>
    
    <div className="flex flex-col items-center relative z-10">
      <div className="relative">
        <div className="w-24 h-24 border-2 border-white/5 rounded-[2rem] flex items-center justify-center backdrop-blur-3xl shadow-2xl overflow-hidden">
           <Loader className="h-10 w-10 animate-spin text-med-success" />
           <div className="absolute inset-0 bg-gradient-to-t from-med-success/10 to-transparent"></div>
        </div>
        <div className="absolute -inset-4 bg-med-success/20 rounded-[2.5rem] blur-2xl animate-pulse -z-10"></div>
      </div>
      <div className="mt-10 flex flex-col items-center">
         <h2 className="text-sm font-black text-white tracking-[0.4em] uppercase italic">{i18n.t('appInit.title')}</h2>
         <p className="mt-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest italic opacity-60">{i18n.t('appInit.subtitle')}</p>
      </div>
    </div>
  </div>
);
const AppWithToasts = () => {
  const toastContext = useToast();
  const showToast = toastContext?.showToast;
  
  // ✅ Phase 1: Performance Detection (Lite Mode for Android 10 / 2GB RAM)
  usePerformanceDetection();
  const isPerformanceMode = usePerformanceMode();

  // Propager le JWT vers la socket au démarrage
  useSocketAuth();

  // Synchroniser la classe CSS du body avec le mode performance
  useEffect(() => {
    if (isPerformanceMode) {
      document.body.classList.add('performance-mode');
    } else {
      document.body.classList.remove('performance-mode');
    }
  }, [isPerformanceMode]);

  useEffect(() => {
    // Initialiser le monitoring Sentry
    monitoring.init();

    // API client est automatiquement initialisé

    const onReconnected = () => {
      showToast?.(i18n.t('notifications.network.online'), 'success');
      monitoring.captureMessage('Connexion rétablie', 'info');
    };

    const onReconnectFailed = () => {
      showToast?.(i18n.t('appInit.reconnectFailed'), 'error');
      monitoring.captureMessage('Échec de reconnexion', 'error');
    };

    connectionService.on('reconnected', onReconnected);
    connectionService.on('reconnectFailed', onReconnectFailed);

    return () => {
      connectionService.off('reconnected', onReconnected);
      connectionService.off('reconnectFailed', onReconnectFailed);
      connectionService.destroy();
    };
  }, [showToast]);

  return (
    <QueryProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AppProvider>
            <SessionProvider>
              <CallProvider>
                <SocketProvider>
                  <MotionConfig reducedMotion={isPerformanceMode ? "always" : "user"}>
                  <div className="fixed top-4 left-4 z-50 hidden md:block">
                    <ConnectionStatus />
                  </div>
                  <OfflineNotification />
                <CustomToaster />
                <PWAInstallPrompt />
                <PWAUpdatePrompt />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Auth Routes */}
                    <Route element={<AuthLayout />}>
                      <Route path="/login" element={<Login />} />
                      <Route path="/login/clinic" element={<ClinicLogin />} />
                      <Route path="/clinic/login" element={<ClinicLogin />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/mfa-setup" element={<MFASetup />} />
                      <Route path="/password-reset" element={<PasswordReset />} />
                      <Route path="/auth/callback" element={<SSOCallback />} />
                    </Route>
                    {/* Root Redirect */}
                    <Route path="/" element={<AuthRedirect />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                    {/* Patient Routes */}
                    <Route
                      path="/patient"
                      element={
                        <ProtectedRoute requiredRole="patient">
                          <DashboardLayout userType="patient" />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<PatientDashboard />} />
                      <Route path="appointments" element={<PatientAppointments />} />
                      <Route path="appointments/new" element={<Navigate to="/patient/doctors" replace />} />
                      <Route path="consultation" element={<PatientConsultation />} />
                      <Route path="consultation/:id" element={<PatientConsultation />} />
                      <Route path="waiting-room" element={<PatientWaitingRoom />} />
                      <Route path="waiting-room/:id" element={<PatientWaitingRoom />} />
                      <Route path="medical-records" element={<PatientMedicalRecords />} />
                      <Route path="messages" element={<PatientMessages />} />
                      <Route path="prescriptions" element={<PatientPrescriptions />} />
                      <Route path="doctors" element={<DoctorsList />} />
                      <Route path="checkout/:teleconsultationId" element={<PatientCheckout />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                    {/* Doctor Routes */}
                    <Route
                      path="/doctor"
                      element={
                        <ProtectedRoute requiredRole="doctor">
                          <DashboardLayout userType="doctor" />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<DoctorDashboard />} />
                      <Route path="appointments" element={<DoctorAppointments />} />
                      {/* Unified Teleconsultation Routes */}
                      <Route
                        path="teleconsultation"
                        element={<Navigate to="/doctor/teleconsultation/new" replace />}
                      />
                      <Route path="teleconsultation/new" element={<DoctorNewConsultation />} />
                      <Route path="teleconsultation/:id" element={<DoctorTeleconsultation />} />
                      {/* Patient Records Routes */}
                      <Route path="patient-records/:id" element={<DoctorPatientRecords />} />
                      <Route path="patient-records/list" element={<DoctorPatientList />} />
                      <Route path="messages" element={<DoctorMessages />} />
                      <Route path="directory" element={<DoctorDirectory />} />
                      <Route path="prescriptions" element={<DoctorPrescriptions />} />
                      <Route path="waiting-room" element={<DoctorWaitingRoom />} />
                      <Route path="peer-lobby/:id" element={<PeerConsultationLobby />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                    {/* Full Screen Onboarding */}
                    <Route 
                      path="/doctor/onboarding" 
                      element={
                        <ProtectedRoute requiredRole="doctor">
                          <DoctorOnboarding />
                        </ProtectedRoute>
                      } 
                    />
                    {/* Nurse Routes */}
                    <Route
                      path="/nurse"
                      element={
                        <ProtectedRoute requiredRole="nurse">
                          <DashboardLayout userType="nurse" />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<NurseDashboard />} />
                      <Route path="triage" element={<TriageQueue />} />
                      <Route path="records" element={<DoctorPatientList />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                    {/* Clinic Structure Routes */}
                    <Route
                      path="/clinic"
                      element={
                        <ProtectedRoute requiredRole="clinic">
                          <DashboardLayout userType="clinic" />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<ClinicDashboard />} />
                      <Route path="queue" element={<ClinicQueuePage />} />
                      <Route path="triage" element={<ClinicQueuePage />} />
                      <Route path="billing" element={<ClinicBillingPage />} />
                      <Route path="referrals" element={<ReferralsManager />} />
                      <Route path="practitioners" element={<ClinicPractitioners />} />
                      <Route path="workspaces" element={<ClinicWorkspaces />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<ClinicSettings />} />
                      <Route path="dme" element={<PatientDMEPage />} />
                      <Route path="dme/:patientId" element={<PatientDMEPage />} />
                      <Route path="display" element={<QueueDisplayTV />} />
                    </Route>
                    {/* Route Kiosk Plein Écran pour Écran TV Salle d'attente */}
                    <Route
                      path="/clinic/tv"
                      element={
                        <ProtectedRoute requiredRole="clinic">
                          <QueueDisplayTV />
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin Routes */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <DashboardLayout userType="admin" />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="monitoring" element={<MonitoringDashboard />} />
                      <Route path="moderation" element={<ConsultationModeration />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="doctors" element={<AdminDoctors />} />
                      <Route path="services" element={<AdminServices />} />
                      <Route path="financials" element={<AdminFinancialReports />} />
                      <Route path="consultations" element={<AdminConsultations />} />
                      <Route path="prescriptions" element={<AdminPrescriptions />} />
                      <Route path="communications" element={<AdminCommunications />} />
                      <Route path="security" element={<AdminSecurity />} />
                      <Route path="permissions" element={<AdminPermissions />} />
                      <Route path="data-export" element={<AdminDataExport />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />
                    </Route>
                    {/* Common Authenticated Routes */}
                    <Route
                      path="/notifications"
                      element={
                        <ProtectedRoute>
                          <NotificationsPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Fallback Route */}
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <NotFoundPage />
                        </ProtectedRoute>
                      }
                    />{' '}
                  </Routes>
                </Suspense>
              </MotionConfig>
              </SocketProvider>
              </CallProvider>
              </SessionProvider>
              </AppProvider>
              </AuthProvider>
              </Router>
              </QueryProvider>
              );
              }

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AppWithToasts />
      </ToastProvider>
    </ErrorBoundary>
  );
}

// Export direct de l'App (ErrorBoundary déjà intégré)
export default App;
